import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useEmployerProfile } from './EmployerProfileContext';
import NotificationBell from './NotificationBell';
import './EmployerDashboard.css';
import PostingEditor from './PostingEditor';
import ResumeAttachment from './ResumeAttachment';

const STATUS_LABEL = { draft: 'Draft', active: 'Live', closed: 'Closed' };
const APP_STATUS_LABEL = { applied: 'Applied', reviewing: 'Reviewing', interview: 'Interview', offer: 'Offer', rejected: 'Not Selected', withdrawn: 'Withdrawn' };
const APP_STATUS_OPTIONS = ['applied', 'reviewing', 'interview', 'offer', 'rejected'];
const BRANCH_OPTIONS = ['U.S. Army', 'U.S. Navy', 'U.S. Air Force', 'U.S. Marine Corps', 'U.S. Coast Guard', 'U.S. Space Force'];
// Matches the exact clearance option strings from Dashboard.jsx's edit form
// (the actual profiles.clearance value domain) -- not job_postings'
// independent 'TS/SCI'/'Secret'/'Public Trust' enum above. An equality
// filter against a value like 'TS/SCI' would never match a stored
// "Top Secret / SCI" or "TS/SCI w/ CI Poly".
const CANDIDATE_CLEARANCE_LEVELS = ['Public Trust', 'Secret', 'Top Secret', 'Top Secret / SCI', 'TS/SCI w/ CI Poly', 'TS/SCI w/ Full Scope Poly'];
const LOOKING_STATUS_LABEL = { actively: 'Actively looking', open: 'Open to conversations', notlooking: 'Not currently looking' };

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7)  return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  return `${Math.floor(diff / 30)}mo ago`;
}

export default function EmployerDashboard() {
  const { employerProfile, loading, refreshEmployerProfile } = useEmployerProfile();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(employerProfile);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error

  const [postings, setPostings] = useState([]);
  const [postingsLoading, setPostingsLoading] = useState(true);
  const [editingPosting, setEditingPosting] = useState(null); // null | 'new' | posting row
  const [postingError, setPostingError] = useState(null);
  const [closingPosting, setClosingPosting] = useState(null);
  const closingRef = useRef(false);

  // Phase 3: applicant pipeline. Fetched once, unfiltered -- RLS already
  // scopes this to applications against postings this employer owns, so no
  // explicit employer_id filter is needed here.
  const [applications, setApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [expandedApplicantsFor, setExpandedApplicantsFor] = useState(null);
  const [applicationUpdates, setApplicationUpdates] = useState({});
  const pendingApplicationUpdates = useRef(new Set());

  useEffect(() => { setDraft(employerProfile); }, [employerProfile]);

  const fetchApplications = useCallback(async () => {
    if (!employerProfile.id) return;
    setApplicationsLoading(true);
    const { data } = await supabase
      .from('applications')
      .select('id, job_posting_id, applicant_id, status, cover_note, resume_url, resume_name, created_at, profiles(first_name, last_name, branch, rank, occupation_title, bio)')
      .order('created_at', { ascending: false });
    setApplications(data || []);
    setApplicationsLoading(false);
  }, [employerProfile.id]);

  useEffect(() => { fetchApplications(); }, [fetchApplications]);

  const applicantsByPosting = {};
  applications.forEach(a => {
    (applicantsByPosting[a.job_posting_id] ||= []).push(a);
  });

  // Phase 4: candidate discovery. search_candidate_profiles is the
  // enforcement point -- it only returns veterans whose visibility settings
  // actually mark them recruiter-visible, masking clearance/certs/skills
  // per-field rather than relying on the client to behave (the raw
  // `profiles` table itself is still broadly readable, so this must not
  // become a plain .from('profiles').select() down the line).
  const [candidateKeyword,   setCandidateKeyword]   = useState('');
  const [candidateBranch,    setCandidateBranch]    = useState('');
  const [candidateClearance, setCandidateClearance] = useState('');
  const [candidates,         setCandidates]         = useState([]);
  const [candidatesLoading,  setCandidatesLoading]  = useState(true);
  const [candidateError,     setCandidateError]     = useState('');
  const searchRequest = useRef(0);
  const [messagingCandidateId, setMessagingCandidateId] = useState(null);
  const [messageError, setMessageError] = useState('');

  const searchCandidates = useCallback(async () => {
    const request = ++searchRequest.current;
    setCandidatesLoading(true);
    setCandidateError('');
    setMessageError('');
    try {
      const { data, error } = await supabase.rpc('search_candidate_profiles', {
        p_keyword: candidateKeyword.trim() || null,
        p_branch: candidateBranch || null,
        p_clearance: candidateClearance || null,
      });
      if (error) throw error;
      if (request === searchRequest.current) setCandidates(data || []);
    } catch {
      if (request === searchRequest.current) {
        setCandidates([]);
        setCandidateError('Candidate search is unavailable right now. Please try again.');
      }
    } finally {
      if (request === searchRequest.current) setCandidatesLoading(false);
    }
  }, [candidateKeyword, candidateBranch, candidateClearance]);

  useEffect(() => { searchCandidates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const messageCandidate = async (candidate) => {
    setMessagingCandidateId(candidate.id);
    setMessageError('');
    try {
      const { data: convId, error } = await supabase.rpc('get_or_create_conversation', { other_user_id: candidate.id });
      if (error || !convId) throw error || new Error('No conversation returned');
      navigate('/messages', {
        state: {
          openConversation: {
            id: convId,
            otherId: candidate.id,
            name: `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
            initials: `${candidate.first_name?.[0] || ''}${candidate.last_name?.[0] || ''}`.toUpperCase(),
          },
        },
      });
    } catch {
      setMessageError(`Could not open a conversation with ${candidate.first_name || 'this candidate'}. Please try again.`);
    } finally {
      setMessagingCandidateId(null);
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    const application = applications.find(a => a.id === applicationId);
    if (!application || application.status === 'withdrawn' || application.status === status ||
        !APP_STATUS_OPTIONS.includes(status) || pendingApplicationUpdates.current.has(applicationId)) return;
    pendingApplicationUpdates.current.add(applicationId);
    setApplicationUpdates(prev => ({ ...prev, [applicationId]: { saving: true, error: '' } }));
    let saved = false;
    let saveError = '';
    try {
      const { data, error } = await supabase.from('applications')
        .update({ status })
        .eq('id', applicationId)
        .eq('status', application.status)
        .select('id, status')
        .single();
      if (error || data?.id !== applicationId || data.status !== status) throw new Error('Save not confirmed');
      saved = true;
      setApplications(prev => prev.map(a => a.id === applicationId ? { ...a, status: data.status } : a));

      const { error: notificationError } = await supabase.from('notifications').insert({
        user_id: application.applicant_id,
        actor_id: employerProfile.id,
        type: 'application_status_change',
        entity_id: applicationId,
      });
      if (notificationError) throw new Error('Notification not confirmed');
    } catch {
      saveError = saved
        ? 'Status saved, but we could not confirm the applicant notification.'
        : 'We could not confirm this status change. Select a status to retry, or refresh to check for changes.';
    } finally {
      pendingApplicationUpdates.current.delete(applicationId);
      setApplicationUpdates(prev => ({ ...prev, [applicationId]: { saving: false, error: saveError } }));
    }
  };

  const fetchPostings = useCallback(async () => {
    if (!employerProfile.id) return;
    setPostingsLoading(true);
    const { data } = await supabase
      .from('job_postings')
      .select('*')
      .eq('employer_id', employerProfile.id)
      .order('created_at', { ascending: false });
    setPostings(data || []);
    setPostingsLoading(false);
  }, [employerProfile.id]);

  useEffect(() => { fetchPostings(); }, [fetchPostings]);

  const openNewPosting = () => { setPostingError(null); setEditingPosting('new'); };
  const openEditPosting = posting => { setPostingError(null); setEditingPosting(posting); };
  const closePostingEditor = () => setEditingPosting(null);
  const setPostingStatus = async (id, status) => {
    if (status !== 'closed' || closingRef.current) return;
    closingRef.current = true; setClosingPosting(id); setPostingError(null);
    try {
      const { data, error } = await supabase.from('job_postings').update({status})
        .eq('id',id).eq('employer_id',employerProfile.id).select('id,status').single();
      if (error || data?.id !== id || data.status !== status) throw Error();
      await fetchPostings();
    } catch { setPostingError('We could not confirm the posting was closed. Refresh or retry before assuming it is no longer live.'); }
    finally { closingRef.current=false; setClosingPosting(null); }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const d = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaveStatus('saving');
    const { error } = await supabase
      .from('employer_profiles')
      .update({
        company_name: draft.companyName?.trim() || employerProfile.companyName,
        company_website: draft.companyWebsite?.trim() || null,
        industry: draft.industry?.trim() || null,
        company_size: draft.companySize || null,
        contact_name: draft.contactName?.trim() || null,
        contact_title: draft.contactTitle?.trim() || null,
      })
      .eq('id', employerProfile.id);

    if (error) {
      setSaveStatus('error');
      return;
    }
    await refreshEmployerProfile();
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  };

  if (loading) {
    return <div className="empd-root empd-loading">Loading…</div>;
  }

  return (
    <div className="empd-root">
      <nav className="empd-nav">
        <Link className="empd-nav-logo" to="/">
          Vet<span>Qualified</span> <span className="empd-nav-tag">for Employers</span>
        </Link>
        <div className="empd-nav-right">
          <NotificationBell userId={employerProfile.id} />
          <button className="empd-logout" onClick={logout}>Sign Out</button>
        </div>
      </nav>

      <div className="empd-shell">
        <h1 className="empd-title">{employerProfile.companyName || 'Company Profile'}</h1>
        <p className="empd-subtitle">Manage your company profile, job postings, applicants, and candidate search.</p>

        <div className="empd-card">
          <div className="empd-card-header">
            <h2 className="empd-card-title">Company Profile</h2>
          </div>
          <div className="empd-form">
            <div className="empd-field">
              <label>Company Name</label>
              <input type="text" value={draft.companyName || ''} onChange={e => d('companyName', e.target.value)} />
            </div>
            <div className="empd-row">
              <div className="empd-field">
                <label>Website</label>
                <input type="text" value={draft.companyWebsite || ''} onChange={e => d('companyWebsite', e.target.value)} placeholder="acme.com" />
              </div>
              <div className="empd-field">
                <label>Company Size</label>
                <select value={draft.companySize || ''} onChange={e => d('companySize', e.target.value)}>
                  <option value="">Select…</option>
                  <option value="1-10">1–10</option>
                  <option value="11-50">11–50</option>
                  <option value="51-200">51–200</option>
                  <option value="201-1000">201–1,000</option>
                  <option value="1000+">1,000+</option>
                </select>
              </div>
            </div>
            <div className="empd-field">
              <label>Industry</label>
              <input type="text" value={draft.industry || ''} onChange={e => d('industry', e.target.value)} />
            </div>
            <div className="empd-row">
              <div className="empd-field">
                <label>Contact Name</label>
                <input type="text" value={draft.contactName || ''} onChange={e => d('contactName', e.target.value)} />
              </div>
              <div className="empd-field">
                <label>Contact Title</label>
                <input type="text" value={draft.contactTitle || ''} onChange={e => d('contactTitle', e.target.value)} />
              </div>
            </div>
            <button className="empd-save-btn" onClick={handleSave} disabled={saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving…' : saveStatus === 'saved' ? 'Saved ✓' : 'Save Changes'}
            </button>
            {saveStatus === 'error' && <div className="empd-error">Could not save. Please try again.</div>}
          </div>
        </div>

        <div className="empd-card">
          <div className="empd-card-header">
            <h2 className="empd-card-title">Job Postings</h2>
            {editingPosting === null && (
              <button className="empd-new-btn" onClick={openNewPosting}>+ New Posting</button>
            )}
          </div>

          {editingPosting === null && (
            <>
              {postingsLoading && <p className="empd-planned-body">Loading postings…</p>}
              {!postingsLoading && postings.length === 0 && (
                <p className="empd-planned-body">You haven't posted any roles yet. Postings marked "Live" appear on the VetQualified Jobs page for veterans to see and message you about.</p>
              )}
              {!postingsLoading && postings.length > 0 && (
                <div className="empd-posting-list">
                  {postings.map(p => {
                    const applicants = applicantsByPosting[p.id] || [];
                    return (
                    <div key={p.id}>
                      <div className="empd-posting-row">
                        <div className="empd-posting-main">
                          <div className="empd-posting-title-row">
                            <span className="empd-posting-title">{p.title}</span>
                            <span className={`empd-status-pill ${p.status}`}>{p.is_test ? 'Practice · ' : ''}{STATUS_LABEL[p.status]}</span>
                          </div>
                          <p className="empd-posting-summary">{p.summary || p.description || 'Add a short summary when you edit this posting.'}</p>
                          <div className="empd-posting-meta">
                            {p.location || 'Location not listed'} · {p.remote_type} · {p.clearance_required}
                          </div>
                        </div>
                        <div className="empd-posting-actions">
                          <button className="empd-link-btn" onClick={() => setExpandedApplicantsFor(v => v === p.id ? null : p.id)}>
                            {expandedApplicantsFor === p.id ? 'Hide' : `Applicants (${applicants.length})`}
                          </button>
                          <button className="empd-link-btn" onClick={() => openEditPosting(p)}>Edit</button>
                          {p.status !== 'active' && (
                            <button className="empd-link-btn" onClick={() => openEditPosting(p)}>Publish</button>
                          )}
                          {p.status === 'active' && (
                            <button className="empd-link-btn" disabled={closingPosting === p.id} onClick={() => setPostingStatus(p.id, 'closed')}>Close</button>
                          )}
                        </div>
                      </div>

                      {expandedApplicantsFor === p.id && (
                        <div className="empd-applicants-panel">
                          {applicationsLoading && <p className="empd-planned-body">Loading applicants…</p>}
                          {!applicationsLoading && applicants.length === 0 && (
                            <p className="empd-planned-body">No applicants yet.</p>
                          )}
                          {applicants.map(app => (
                            <div className="empd-applicant-row" key={app.id}>
                              <div className="empd-applicant-info">
                                <div className="empd-applicant-name">
                                  {app.profiles?.first_name} {app.profiles?.last_name}
                                  <span className="empd-applicant-meta"> · {app.profiles?.branch}{app.profiles?.rank ? ` · ${app.profiles.rank}` : ''} · Applied {timeAgo(app.created_at)}</span>
                                </div>
                                {app.profiles?.occupation_title && (
                                  <div className="empd-applicant-meta">{app.profiles.occupation_title}</div>
                                )}
                                <ResumeAttachment url={app.resume_url} name={app.resume_name}
                                  applicantName={`${app.profiles?.first_name || ''} ${app.profiles?.last_name || ''}`.trim() || 'applicant'} />
                                {app.cover_note && <p className="empd-applicant-note">&ldquo;{app.cover_note}&rdquo;</p>}
                              </div>
                              <div className="empd-applicant-status">
                                {app.status === 'withdrawn' ? (
                                  <span className="empd-app-status-pill withdrawn">Withdrawn</span>
                                ) : (
                                  <select
                                    className="empd-applicant-status-select"
                                    aria-label={`Application status for ${`${app.profiles?.first_name || ''} ${app.profiles?.last_name || ''}`.trim() || 'applicant'}`}
                                    aria-describedby={`application-feedback-${app.id}`}
                                    value={app.status}
                                    disabled={!!applicationUpdates[app.id]?.saving}
                                    onChange={e => updateApplicationStatus(app.id, e.target.value)}
                                  >
                                    {APP_STATUS_OPTIONS.map(s => <option key={s} value={s}>{APP_STATUS_LABEL[s]}</option>)}
                                  </select>
                                )}
                                <div id={`application-feedback-${app.id}`}>
                                  {applicationUpdates[app.id]?.saving && <p role="status" className="empd-applicant-meta">Saving status…</p>}
                                  {applicationUpdates[app.id]?.error && <p role="alert" className="empd-error">{applicationUpdates[app.id].error}</p>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {postingError && <p role="alert" className="empd-error">{postingError}</p>}
          {editingPosting !== null && (
            <PostingEditor key={editingPosting === 'new' ? 'new' : editingPosting.id}
              posting={editingPosting === 'new' ? null : editingPosting} employerId={employerProfile.id}
              company={employerProfile.companyName} onCancel={closePostingEditor}
              onSaved={() => { closePostingEditor(); fetchPostings(); }} />
          )}
        </div>

        <div className="empd-card">
          <div className="empd-card-header">
            <h2 className="empd-card-title">Candidate Search</h2>
          </div>
          <p className="empd-planned-body" style={{ marginBottom: '1rem' }}>
            Only shows veterans who set their profile visibility to recruiters — respects each candidate's own privacy choices.
          </p>
          <div className="empd-candidate-filters">
            <input
              type="text"
              placeholder="Keyword — occupation, skills, career goal…"
              value={candidateKeyword}
              onChange={e => setCandidateKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchCandidates()}
              className="empd-candidate-search-input"
            />
            <select value={candidateBranch} onChange={e => setCandidateBranch(e.target.value)}>
              <option value="">All Branches</option>
              {BRANCH_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select value={candidateClearance} onChange={e => setCandidateClearance(e.target.value)}>
              <option value="">All Clearances</option>
              {CANDIDATE_CLEARANCE_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button className="empd-new-btn" onClick={searchCandidates}>Search</button>
          </div>

          {candidateError && <p role="alert" className="empd-error">{candidateError}</p>}
          {messageError && <p role="alert" className="empd-error">{messageError}</p>}
          {candidatesLoading && <p className="empd-planned-body">Searching…</p>}
          {!candidatesLoading && !candidateError && candidates.length === 0 && (
            <p className="empd-planned-body">No candidates match right now — try broadening your filters. Results only include veterans who've opted into recruiter visibility.</p>
          )}
          {!candidatesLoading && candidates.length > 0 && (
            <div className="empd-candidate-list">
              {candidates.map(c => (
                <div className="empd-candidate-card" key={c.id}>
                  <div className="empd-candidate-avatar">
                    {c.avatar_url
                      ? <img src={c.avatar_url} alt="" />
                      : `${c.first_name?.[0] || ''}${c.last_name?.[0] || ''}`.toUpperCase()}
                  </div>
                  <div className="empd-candidate-info">
                    <div className="empd-candidate-name-row">
                      <span className="empd-candidate-name">{c.first_name} {c.last_name}</span>
                      {c.looking_status && c.looking_status !== 'actively' && (
                        <span className="empd-candidate-looking">{LOOKING_STATUS_LABEL[c.looking_status]}</span>
                      )}
                    </div>
                    <div className="empd-applicant-meta">
                      {c.branch}{c.rank ? ` · ${c.rank}` : ''}{c.occupation_title ? ` · ${c.occupation_title}` : ''}
                    </div>
                    {c.career_goal && <div className="empd-applicant-meta">Seeking: {c.career_goal}{c.location ? ` · ${c.location}` : ''}</div>}
                    <details className="empd-candidate-details">
                      <summary>
                        <span className="empd-expand-label">Expand details</span>
                        <span className="empd-collapse-label">Collapse details</span>
                        <span className="empd-sr-only"> for {c.first_name} {c.last_name}</span>
                      </summary>
                    {c.clearance && <span className="empd-app-status-pill" style={{ color: '#C9A84C', background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', marginTop: '0.4rem', display: 'inline-block' }}>{c.clearance}</span>}
                    {c.bio && <p className="empd-applicant-note">{c.bio}</p>}
                    {(c.tech_skills?.length > 0 || c.lead_skills?.length > 0) && (
                      <div className="empd-candidate-tags">
                        {[...(c.tech_skills || []), ...(c.lead_skills || [])].slice(0, 6).map(s => <span key={s} className="empd-candidate-tag">{s}</span>)}
                      </div>
                    )}
                      {!c.clearance && !c.bio && !c.tech_skills?.length && !c.lead_skills?.length && (
                        <p className="empd-applicant-meta">No additional details shared.</p>
                      )}
                    </details>
                  </div>
                  <button
                    className="empd-save-btn"
                    style={{ flexShrink: 0 }}
                    onClick={() => messageCandidate(c)}
                    disabled={messagingCandidateId === c.id}
                  >
                    {messagingCandidateId === c.id ? 'Starting…' : 'Message'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
